import type { SigningView } from "./signing";
import { z } from "zod";
import { changesOf } from "./zod-util";

/**
 * Change orders (spec §8): approved insurance supplements, added work, or credits. They change the
 * contract. The customer signs only when their out-of-pocket goes up; otherwise the office approves.
 */

export const CHANGE_ORDER_KINDS = ["supplement", "added_work", "credit"] as const;
export type ChangeOrderKind = (typeof CHANGE_ORDER_KINDS)[number];
export const CHANGE_ORDER_KIND_LABELS: Record<ChangeOrderKind, string> = {
  supplement: "Insurance supplement",
  added_work: "Added work (customer pays)",
  credit: "Credit to the customer",
};

export type ChangeOrderStatus = "draft" | "sent" | "approved" | "signed" | "declined" | "void";

export interface ChangeOrderLine {
  description: string;
  amountCents: number;
}

/**
 * What a change order does to the contract and to what the customer pays themselves:
 * - supplement: the carrier pays it (approved supplement); the contract goes up, out-of-pocket doesn't.
 * - added work: the customer pays; both go up.
 * - credit: lines are amounts off; both go down.
 */
export function changeOrderEffect(kind: ChangeOrderKind, lines: ChangeOrderLine[]): { contractDeltaCents: number; outOfPocketDeltaCents: number; needsSignature: boolean } {
  for (const l of lines) if (!Number.isInteger(l.amountCents) || l.amountCents < 0) throw new Error("Line amounts are whole, positive cents.");
  const sum = lines.reduce((s, l) => s + l.amountCents, 0);
  const contractDeltaCents = kind === "credit" ? -sum : sum;
  const outOfPocketDeltaCents = kind === "supplement" ? 0 : contractDeltaCents;
  return { contractDeltaCents, outOfPocketDeltaCents, needsSignature: outOfPocketDeltaCents > 0 };
}

export interface ChangeOrderItem {
  id: string;
  number: number;
  jobId: string;
  kind: ChangeOrderKind;
  title: string;
  lines: ChangeOrderLine[];
  contractDeltaCents: number;
  outOfPocketDeltaCents: number;
  needsSignature: boolean;
  status: ChangeOrderStatus;
  note: string;
  signatureName: string | null;
  signedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  pdfUrl: string | null;
  createdAt: string;
  /** Only right after sending or making a new link. */
  signUrl?: string;
}

export const changeOrderInputSchema = z.object({
  kind: z.enum(CHANGE_ORDER_KINDS),
  title: z.string().trim().max(120).default(""),
  lines: z.array(z.object({ description: z.string().trim().min(1, "Describe the line").max(4000), amountCents: z.number().int().min(0).max(100_000_000) })).min(1, "Add a line").max(50),
  note: z.string().trim().max(2000).default(""),
});
export const changeOrderUpdateSchema = changesOf(changeOrderInputSchema);

export interface PublicChangeOrder {
  number: number;
  status: ChangeOrderStatus;
  company: { name: string; phone: string; logoUrl: string | null; primaryColor: string };
  customerName: string;
  address: string;
  kind: ChangeOrderKind;
  title: string;
  lines: ChangeOrderLine[];
  contractDeltaCents: number;
  outOfPocketDeltaCents: number;
  note: string;
  signatureName: string | null;
  signedAt: string | null;
  /** Guided signing (sent since it started): the spots to tap. The document is at /api/public/change-orders/:token/document.pdf. */
  signing?: SigningView | null;
}

export const changeOrderSignSchema = z.object({
  name: z.string().trim().min(2, "Type your full name").max(120),
  consent: z.literal(true),
  /** Guided signing: typed initials and the drawn signature (PNG data URL). Required when the change order has spots to tap. */
  initials: z.string().trim().min(1).max(8).optional(),
  signature: z.string().max(400_000).regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, "Draw your signature").optional(),
});
export const changeOrderDeclineSchema = z.object({ reason: z.string().trim().max(2000).default("") });
